/**
 * WhatsApp order form for Aromas de Brisa (`snippets/ab-order-dialog.liquid`).
 *
 * `[data-ab-open-order]` loads the current cart and opens the form. On submit it
 * validates the fields, saves the optional email as a Shopify customer (newsletter form)
 * and opens WhatsApp with the full order: items, totals, name, address, payment and notes.
 * It also publishes `ab_order_form_opened` and `ab_whatsapp_order` for ad tracking.
 */

import { publish, eventId } from '@theme/ab-tracking';

const STORAGE_KEY = 'ab-order-contact';
const money = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

/** @param {number} cents */
const formatMoney = (cents) => money.format(Math.round(cents / 100));

/** @type {{ items: any[], total_price: number } | null} */
let cart = null;

function getDialog() {
  const dialog = document.getElementById('AbOrderDialog');
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

/** @param {HTMLDialogElement} dialog */
function cardTotal(dialog) {
  if (!cart) return 0;
  const surcharge = Number(dialog.dataset.surcharge) || 0;
  let overrides = {};
  try {
    overrides = JSON.parse(dialog.querySelector('[data-ab-card-overrides]')?.textContent || '{}');
  } catch {
    overrides = {};
  }
  return cart.items.reduce((sum, item) => {
    const unit = overrides[item.product_id] ?? Math.round((item.final_price * (100 + surcharge)) / 100);
    return sum + unit * item.quantity;
  }, 0);
}

/** @param {HTMLDialogElement} dialog */
function renderSummary(dialog) {
  const target = dialog.querySelector('[data-ab-order-summary]');
  if (!target || !cart) return;

  if (!cart.items.length) {
    target.textContent = 'Tu carrito está vacío. Agregá un combo o producto para generar el pedido.';
    return;
  }

  const list = document.createElement('ul');
  for (const item of cart.items) {
    const li = document.createElement('li');
    li.textContent = `${item.quantity} x ${item.title} — ${formatMoney(item.final_line_price)}`;
    list.append(li);
  }

  const payment = dialog.querySelector('input[name="payment"]:checked')?.value;
  const total = document.createElement('p');
  total.className = 'ab-order__total';
  total.textContent =
    payment === 'Tarjeta de crédito'
      ? `Total con tarjeta: ${formatMoney(cardTotal(dialog))}`
      : `Total en efectivo o transferencia: ${formatMoney(cart.total_price)}`;

  target.replaceChildren(list, total);
}

/** @param {HTMLDialogElement} dialog */
function restoreContact(dialog) {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    for (const name of ['name', 'address', 'email']) {
      const input = dialog.querySelector(`[name="${name}"]`);
      if (input instanceof HTMLInputElement && !input.value && saved[name]) input.value = saved[name];
    }
  } catch {
    // Storage can be unavailable (private mode); the form still works.
  }
}

async function openOrder() {
  const dialog = getDialog();
  if (!dialog) return;

  try {
    cart = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((res) => res.json());
  } catch {
    cart = { items: [], total_price: 0 };
  }

  // Close the cart drawer first so the order form gets focus on its own.
  document.querySelector('theme-drawer')?.close?.();

  restoreContact(dialog);
  renderSummary(dialog);
  if (!dialog.open) dialog.showModal();

  if (cart.items.length) {
    publish('ab_order_form_opened', { event_id: eventId(), ...orderPayload(cart.total_price) });
  }
}

/**
 * Order data for tracking: value in pesos, currency and line items.
 * @param {number} totalCents
 */
function orderPayload(totalCents) {
  const items = cart?.items ?? [];
  return {
    value: Math.round(totalCents) / 100,
    currency: 'ARS',
    num_items: items.reduce((sum, item) => sum + item.quantity, 0),
    contents: items.map((item) => ({
      id: String(item.variant_id),
      product_id: String(item.product_id),
      title: item.title,
      quantity: item.quantity,
      item_price: item.final_price / 100,
    })),
  };
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLDialogElement} dialog
 */
function buildMessage(form, dialog) {
  const data = new FormData(form);
  const payment = String(data.get('payment'));
  const lines = ['¡Hola Aromas de Brisa! Quiero hacer este pedido:', '', '🛒 *Pedido*'];

  for (const item of cart?.items ?? []) {
    lines.push(`• ${item.quantity} x ${item.title} — ${formatMoney(item.final_line_price)}`);
  }

  lines.push('');
  if (payment === 'Tarjeta de crédito') {
    lines.push(`💳 *Total con tarjeta:* ${formatMoney(cardTotal(dialog))}`);
  } else {
    lines.push(`💵 *Total:* ${formatMoney(cart?.total_price ?? 0)}`);
  }

  lines.push('', `👤 *Nombre:* ${data.get('name')}`, `📍 *Dirección:* ${data.get('address')}`, `💰 *Pago:* ${payment}`);

  const notes = String(data.get('notes') || '').trim();
  if (notes) lines.push(`🕒 *Horario / aclaraciones:* ${notes}`);
  const email = String(data.get('email') || '').trim();
  if (email) lines.push(`📧 *Email:* ${email}`);

  return lines.join('\n');
}

/**
 * Saves the email as a customer with the `pedido-whatsapp` tag (Shopify newsletter form).
 * Best effort: the WhatsApp order never waits on it.
 * @param {FormData} data
 */
function saveCustomer(data) {
  const email = String(data.get('email') || '').trim();
  if (!email) return;

  const body = new FormData();
  body.set('form_type', 'customer');
  body.set('utf8', '✓');
  body.set('contact[email]', email);
  body.set('contact[first_name]', String(data.get('name') || ''));
  body.set('contact[tags]', 'newsletter,pedido-whatsapp');
  fetch('/contact', { method: 'POST', body, credentials: 'same-origin' }).catch(() => {});
}

/** @param {SubmitEvent} event */
function handleSubmit(event) {
  const form = event.target;
  const dialog = getDialog();
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-ab-order-form]') || !dialog) return;
  event.preventDefault();

  const error = form.querySelector('[data-ab-order-error]');
  const valid = form.checkValidity() && (cart?.items.length ?? 0) > 0;
  if (error) error.hidden = valid;
  if (!valid) {
    form.reportValidity();
    return;
  }

  const data = new FormData(form);
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ name: data.get('name'), address: data.get('address'), email: data.get('email') })
    );
  } catch {
    // Ignore storage failures.
  }

  saveCustomer(data);

  const payment = String(data.get('payment'));
  const total = payment === 'Tarjeta de crédito' ? cardTotal(dialog) : cart?.total_price ?? 0;
  publish('ab_whatsapp_order', { event_id: eventId(), payment, ...orderPayload(total) });

  const url = `https://wa.me/${dialog.dataset.whatsapp}?text=${encodeURIComponent(buildMessage(form, dialog))}`;
  // Without `noopener` in the features string, window.open returns the new window (or null when blocked).
  const popup = window.open(url, '_blank');
  if (popup) {
    popup.opener = null;
  } else {
    window.location.href = url;
  }
}

if (!window.abOrderReady) {
  window.abOrderReady = true;

  document.addEventListener('click', (event) => {
    const opener = event.target instanceof Element ? event.target.closest('[data-ab-open-order]') : null;
    if (!opener) return;
    event.preventDefault();
    openOrder();
  });

  document.addEventListener('change', (event) => {
    const dialog = getDialog();
    if (dialog && event.target instanceof HTMLInputElement && event.target.name === 'payment') renderSummary(dialog);
  });

  document.addEventListener('submit', handleSubmit);
}
